// Samtykke til markedsføring (markedsføringsloven § 10) og afmelding.
//  • Hver automatisering er "service" (bekræftelse, påmindelse, kvittering, betaling/rykker – sendes altid, når udsendelsen
//    ikke er spærret), "markedsføring" (tilbud og kampagner – kun til kunder, der har sagt ja) eller "anmeldelse".
//  • "Anmeldelse" (anmodning om en anmeldelse af en ydelse, kunden har købt – se anmeldelse.ts) er sin egen type: den hører
//    til en købt ydelse og er derfor ikke markedsføring, der kræver samtykke (Forbrugerombudsmandens vejledning om
//    anmeldelser og markedsføringsloven § 10 stk. 2 om egne, tilsvarende ydelser). Men den er heller ikke en nødvendig
//    servicebesked: en kunde, der har afmeldt sig, får den ikke, og den har afmeldingslinket i bunden.
//  • Samtykket står på kunden (kunder.markedsfoering). Det kommer fra LatePoint-importen og bookingformularens afkrydsning
//    (felter.markedsfoering = 'ja') – en kunde, der har afmeldt sig, får det ikke igen af sig selv; kun kontoret eller kunden selv kan.
//  • Alle markedsføringsmails får et afmeldingslink i bunden og SMS'er "Afmeld: <link>". Linket har kundens hemmelige token
//    (kunder.afmeld_token) og åbner en offentlig side uden login (#/af/<token>), der siger "Du er afmeldt".
import type { Handling, Workflow } from './automation.ts';

export type Formaal = 'service' | 'markedsfoering' | 'anmeldelse';
export const FORMAAL: [Formaal, string, string][] = [
  ['service', 'Service', 'Bekræftelse, påmindelse, kvittering og betaling – sendes til alle kunder'],
  ['markedsfoering', 'Markedsføring', 'Tilbud og kampagner – kun til kunder, der har sagt ja'],
  ['anmeldelse', 'Anmeldelse', 'Spørg om en anmeldelse af en købt ydelse – ikke til kunder, der har afmeldt sig, og højst én gang pr. 90 dage'],
];

/** Standardreglerne, der ikke er service: tilbud og rabatkode er markedsføring, "Hvordan gik det?" er anmeldelse. */
const STANDARD_FORMAAL: Record<string, Formaal> = { wf_mersalg: 'markedsfoering', wf_velkomst100: 'markedsfoering', wf_feedback: 'anmeldelse' };

/** Automatiseringens formål – gemt på reglen, ellers ud fra standardreglerne (gamle regler uden felt). */
export function formaalFor(w: Pick<Workflow, 'id' | 'formaal'>): Formaal {
  return w.formaal ?? STANDARD_FORMAAL[w.id] ?? 'service';
}

/** Må handlingen sendes? Markedsføring til kunden kræver samtykke; en anmodning om anmeldelse kræver kun, at kunden ikke har
 *  afmeldt sig. Beskeder til steamer og kontor er aldrig markedsføring. */
export function maaSende(w: Pick<Workflow, 'id' | 'formaal'>, h: Pick<Handling, 'til'>, samtykke: boolean, afmeldt = false): boolean {
  if (h.til !== 'kunde') return true;
  const f = formaalFor(w);
  return f === 'service' || (f === 'anmeldelse' ? !afmeldt : samtykke);
}
export const INGEN_SAMTYKKE = 'Kunden har ikke sagt ja til markedsføring';

/** Kundens token i links: 16 tegn 0-9a-f (64 bit tilfældigt, sat af databasen). */
export const gyldigtAfmeldToken = (t: unknown): t is string => typeof t === 'string' && /^[0-9a-f]{16}$/.test(t);
/** Kort link til afmeldingssiden på virksomhedens (eller brandets) adresse, fx https://steamgreen.dk/#/af/1a2b3c4d5e6f7a8b. */
export const afmeldLink = (adresse: string, token: string) => `${adresse.replace(/\/?$/, '/')}#/af/${token}`;

/** Markedsføring og anmodninger om anmeldelse får afmeldingen i bunden: mails en linje med linket, SMS'er "Afmeld: <link>". */
export function medAfmelding(tekst: string, kanal: 'mail' | 'sms', link: string, firma: string, formaal: Formaal = 'markedsfoering'): string {
  if (!link || tekst.includes(link)) return tekst;
  if (kanal === 'sms') return `${tekst.trimEnd()} Afmeld: ${link}`;
  const hvad = formaal === 'anmeldelse' ? 'spørgsmål om anmeldelser, tilbud og nyheder' : 'tilbud og nyheder';
  return `${tekst.trimEnd()}\n\nVil du ikke længere have ${hvad} fra ${firma}? Afmeld her: ${link}`;
}
/** Får kundens beskeder fra reglen afmeldingslinket? */
export const harAfmeldLink = (w: Pick<Workflow, 'id' | 'formaal'>) => formaalFor(w) !== 'service';

/** Kundens samtykke i felterne: ja/nej, hvornår og hvor (bookingsiden eller Mine bookinger). Databasen sætter kolonnerne ud fra
 *  felter.markedsfoering = 'ja' (pixie_kunde_samtykke) – aldrig for en kunde, der har afmeldt sig. */
export type SamtykkeKilde = 'booking' | 'mine';
export const samtykkeFelter = (kilde: SamtykkeKilde, ja = true, nu = new Date().toISOString()): Record<string, string> =>
  ({ markedsfoering: ja ? 'ja' : 'nej', markedsfoering_tid: nu, markedsfoering_kilde: kilde });

/** Har kunden sagt ja til markedsføring? Kolonnen (drift) eller felterne (gamle data i testversionen). */
export function harSamtykke(k: { markedsfoering?: boolean | null; afmeldt?: string | null; felter?: Record<string, string> | null }): boolean {
  if (k.afmeldt) return false;
  return k.markedsfoering ?? k.felter?.markedsfoering === 'ja';
}
